import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
  SparklesIcon,
  PhoneIcon,
  MapPinIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
  UserIcon,
  EyeIcon,
  Square2StackIcon,
  GiftIcon,
  ChatBubbleBottomCenterTextIcon,
  DocumentTextIcon,
  DevicePhoneMobileIcon,
  CreditCardIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import { formatearFechaArgentina } from '../utils/fechas';
import { formatearTextoCuotas } from '../utils/precios';

const formatearPrecio = (precio) => {
  if (!precio || isNaN(precio)) return '$0';
  return `$${Math.round(parseFloat(precio)).toLocaleString('es-AR')}`;
};

/**
 * Parsea una descripción que contenga ítems separados por guiones (-), viñetas (•) o saltos de línea (\n)
 * y devuelve un array limpio de propiedades individuales para mostrarlas en renglones separados.
 */
export function parsearItemsDescripcion(texto) {
  if (!texto || typeof texto !== 'string') return [];

  const lineas = texto.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const items = [];

  lineas.forEach((linea) => {
    // Si la línea contiene múltiples guiones separando características (ej: "-Item 1 -Item 2")
    if (linea.includes(' -') || (linea.startsWith('-') && linea.slice(1).includes('-'))) {
      const partes = linea
        .split(/(?:^|\s+)-\s*/)
        .map((p) => p.trim())
        .filter(Boolean);
      items.push(...partes);
    } else {
      const limpia = linea.replace(/^[-•*]\s*/, '').trim();
      if (limpia) items.push(limpia);
    }
  });

  return items;
}

export const PresupuestoCardImagen = forwardRef(function PresupuestoCardImagen(
  {
    optica,
    printConfig,
    cliente,
    nombreManual,
    telefonoManual,
    cristalesSeleccionados = [],
    lentesContactoSeleccionadas = [],
    armazonesSeleccionados = [],
    descripciones = [],
    totalPublico = 0,
    totalEfectivo = 0,
    descuentoOptica = 10,
    descuentoArmazon = 10,
    tieneArmazonesCacic = false,
    validezDias = 7,
    notasPresupuesto = '',
    mostrarResumenCotizacion = true,
    cuotasSeleccionadas = [],
    textoCuotas: propTextoCuotas = '',
  },
  ref
) {
  const nombreDestinatario = cliente
    ? `${cliente.nombre || ''} ${cliente.apellido || ''}`.trim()
    : nombreManual.trim() || 'Estimado/a Cliente';

  const telefonoDestinatario = cliente?.telefono || telefonoManual || '';
  const textoCuotas = propTextoCuotas || formatearTextoCuotas(cuotasSeleccionadas);

  const fechaHoy = new Date();
  const fechaHoyFormateada = formatearFechaArgentina(fechaHoy) || fechaHoy.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const fechaVencimiento = new Date();
  fechaVencimiento.setDate(fechaVencimiento.getDate() + (parseInt(validezDias, 10) || 7));
  const fechaVencimientoFormateada = formatearFechaArgentina(fechaVencimiento) || fechaVencimiento.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  // Resolver datos de la óptica
  const nombreOptica = printConfig?.nombre?.trim() || optica?.nombre?.trim() || 'ÓPTICA';
  const direccionOptica = printConfig?.direccion?.trim() || '';
  const telefono1 = printConfig?.telefono1?.trim() || '';
  const telefono2 = printConfig?.telefono2?.trim() || '';
  const emailOptica = printConfig?.email?.trim() || '';

  const logoUrlRaw = printConfig?.logo || optica?.logo_url;
  const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const logoUrl = logoUrlRaw?.startsWith('/') ? `${backendUrl}${logoUrlRaw}` : logoUrlRaw;

  return (
    <div
      ref={ref}
      id="presupuesto-card-canvas"
      className="w-[680px] bg-white text-slate-800 p-8 rounded-2xl shadow-xl border border-slate-200 font-sans mx-auto box-border"
      style={{ minHeight: '600px' }}
    >
      {/* 1. HEADER INSTITUCIONAL */}
      <div className="flex items-start justify-between border-b-2 border-slate-100 pb-6 gap-6">
        {/* Bloque Izquierdo: Logo y Datos de la Óptica */}
        <div className="flex items-center gap-5 flex-1 min-w-0">
          {logoUrl ? (
            <div className="flex-shrink-0 flex items-center justify-start max-w-[210px] min-h-[64px]">
              <img
                src={logoUrl}
                alt={nombreOptica}
                crossOrigin="anonymous"
                className="object-contain max-h-[68px] max-w-[200px] w-auto h-auto block"
              />
            </div>
          ) : (
            <div className="flex-shrink-0 h-16 w-16 bg-gradient-to-tr from-blue-600 to-indigo-700 rounded-2xl flex items-center justify-center text-white font-extrabold text-2xl shadow-md">
              {nombreOptica.charAt(0).toUpperCase()}
            </div>
          )}

          {/* Información y Contacto de la Óptica */}
          <div className="flex-1 min-w-0 space-y-1 pl-1">
            <h1 className="text-lg font-black text-slate-900 tracking-tight leading-tight">
              {nombreOptica}
            </h1>
            <div className="text-[11px] text-slate-600 space-y-1.5">
              {direccionOptica && (
                <div className="flex items-center gap-2">
                  <MapPinIcon className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                  <span>{direccionOptica}</span>
                </div>
              )}
              {(telefono1 || telefono2) && (
                <div className="flex items-center gap-2 font-medium text-slate-700">
                  <PhoneIcon className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                  <span>{[telefono1, telefono2].filter(Boolean).join('  •  ')}</span>
                </div>
              )}
              {emailOptica && (
                <div className="flex items-center gap-2 text-slate-600">
                  <EnvelopeIcon className="h-3.5 w-3.5 text-indigo-500 shrink-0" />
                  <span>{emailOptica}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bloque Derecho: Badge de Presupuesto y Fecha */}
        <div className="text-right flex-shrink-0 pl-2">
          <div className="inline-block bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-black px-3.5 py-1.5 rounded-xl uppercase tracking-wider shadow-sm">
            Presupuesto
          </div>
          <div className="text-xs text-slate-500 mt-2 space-y-1">
            <p className="font-semibold text-slate-700">
              Fecha: <span className="font-bold text-slate-900">{fechaHoyFormateada}</span>
            </p>
            <p className="text-[11px] text-amber-800 font-bold bg-amber-50 border border-amber-200/80 px-2 py-0.5 rounded-md inline-block">
              Válido hasta: {fechaVencimientoFormateada}
            </p>
          </div>
        </div>
      </div>

      {/* 2. DATOS DEL CLIENTE / DESTINATARIO */}
      <div className="mt-5 bg-gradient-to-r from-slate-50 to-blue-50/40 p-4 rounded-xl border border-slate-200/80 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-white text-blue-600 rounded-xl shadow-xs border border-slate-100 flex items-center justify-center">
            <UserIcon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Destinatario</p>
            <p className="text-sm font-black text-slate-900">{nombreDestinatario}</p>
          </div>
        </div>

        {telefonoDestinatario && (
          <div className="text-right">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Contacto</p>
            <div className="text-xs font-semibold text-slate-700 font-mono flex items-center justify-end gap-1.5 mt-0.5">
              <DevicePhoneMobileIcon className="h-3.5 w-3.5 text-slate-500 shrink-0" />
              <span>{telefonoDestinatario}</span>
            </div>
          </div>
        )}
      </div>

      {/* 3. DETALLE DE CRISTALES */}
      {cristalesSeleccionados.length > 0 && (
        <div className="mt-6 space-y-3">
          <div className="flex items-center justify-between border-b border-slate-200 pb-2">
            <h2 className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
              <EyeIcon className="h-4 w-4 text-blue-600 shrink-0" />
              <span>Cristales Oftálmicos</span>
            </h2>
            <span className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2.5 py-0.5 rounded-md">
              {cristalesSeleccionados.length} {cristalesSeleccionados.length === 1 ? 'par / opción' : 'opciones'}
            </span>
          </div>

          <div className="space-y-3">
            {cristalesSeleccionados.map((item, idx) => {
              const c = item.cristal;
              const descObj = item.descripcionId
                ? descripciones.find((d) => d.id === parseInt(item.descripcionId, 10))
                : null;

              return (
                <div
                  key={item.cristalId || idx}
                  className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-xs space-y-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-black text-slate-900">
                          {c.tipo_vision || 'Cristal'} {c.marca ? `- ${c.marca}` : ''} {c.producto ? `(${c.producto})` : ''}
                        </span>
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            c.stock
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}
                        >
                          {c.stock ? 'Stock' : 'Laboratorio'}
                        </span>
                      </div>

                      <p className="text-[11px] text-slate-500">
                        {c.material ? `Material: ${c.material}` : 'Material Estándar'}
                        {c.tratamiento ? ` • Tratamiento: ${c.tratamiento}` : ''}
                      </p>
                    </div>

                    <div className="text-right shrink-0">
                      <p className="text-sm font-black text-slate-900">
                        {formatearPrecio(c.precio_publico)}
                      </p>
                    </div>
                  </div>

                  {/* Característica / Beneficios asignados con desglose por renglón */}
                  {descObj && (() => {
                    const itemsDesc = parsearItemsDescripcion(descObj.descripcion);
                    const tituloLimpio = (descObj.titulo || '').replace(/^[-•*]\s*/, '').trim();

                    return (
                      <div className="bg-blue-50/70 border border-blue-100/90 rounded-lg p-2.5 text-[11px] text-blue-950">
                        {tituloLimpio && (
                          <p className="font-bold flex items-center gap-1.5 text-blue-900">
                            <SparklesIcon className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                            <span>{tituloLimpio}</span>
                          </p>
                        )}
                        {itemsDesc.length > 1 ? (
                          <ul className="mt-1.5 space-y-1 text-slate-700 pl-0.5">
                            {itemsDesc.map((it, i) => (
                              <li key={i} className="flex items-start gap-1.5 leading-snug">
                                <span className="text-blue-500 font-bold shrink-0 leading-none mt-0.5">•</span>
                                <span className="flex-1">{it}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-slate-600 mt-0.5 leading-relaxed">
                            {itemsDesc[0] || descObj.descripcion}
                          </p>
                        )}
                      </div>
                    );
                  })()}

                  {/* Notas adicionales */}
                  {item.notas && (
                    <div className="text-[11px] text-slate-600 italic bg-slate-50 px-2.5 py-1.5 rounded-md border border-slate-100 flex items-center gap-1.5">
                      <DocumentTextIcon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                      <span>Nota: {item.notas}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 3b. DETALLE DE LENTES DE CONTACTO */}
      {lentesContactoSeleccionadas.length > 0 && (
        <div className="mt-6 space-y-3">
          <div className="flex items-center justify-between border-b border-slate-200 pb-2">
            <h2 className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
              <SparklesIcon className="h-4 w-4 text-emerald-600 shrink-0" />
              <span>Lentes de Contacto</span>
            </h2>
            <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-md">
              {lentesContactoSeleccionadas.length} {lentesContactoSeleccionadas.length === 1 ? 'opción' : 'opciones'}
            </span>
          </div>

          <div className="space-y-2.5">
            {lentesContactoSeleccionadas.map((item, idx) => {
              const l = item.lente;
              return (
                <div
                  key={item.lenteId || idx}
                  className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-xs space-y-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-black text-slate-900">
                          {l.marca || 'Lente de Contacto'} {l.tipo_vision ? `- ${l.tipo_vision}` : ''}
                        </span>
                        {l.son_esteticas && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-pink-100 text-pink-800">
                            {l.color ? `Color: ${l.color}` : 'Cosmética'}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-500">
                        {l.rango ? `Graduación / Rango: ${l.rango}` : 'Graduación según prescripción'}
                      </p>
                    </div>

                    <div className="text-right shrink-0">
                      <p className="text-sm font-black text-slate-900">
                        {formatearPrecio(l.precio_publico)}
                      </p>
                    </div>
                  </div>

                  {item.notas && (
                    <div className="text-[11px] text-emerald-800 bg-emerald-50/70 px-2.5 py-1.5 rounded-md border border-emerald-100 flex items-center gap-1.5">
                      <DocumentTextIcon className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                      <span>Detalle: {item.notas}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 4. DETALLE DE ARMAZONES */}
      {armazonesSeleccionados.length > 0 && (
        <div className="mt-6 space-y-3">
          <div className="flex items-center justify-between border-b border-slate-200 pb-2">
            <h2 className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
              <Square2StackIcon className="h-4 w-4 text-indigo-600 shrink-0" />
              <span>Armazones / Monturas</span>
            </h2>
            <span className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2.5 py-0.5 rounded-md">
              {armazonesSeleccionados.length} {armazonesSeleccionados.length === 1 ? 'modelo' : 'modelos'}
            </span>
          </div>

          <div className="space-y-2.5">
            {armazonesSeleccionados.map((a, idx) => {
              const descObj = a.descripcionId
                ? descripciones.find((d) => d.id === parseInt(a.descripcionId, 10))
                : null;

              return (
                <div
                  key={a.id || idx}
                  className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-xs space-y-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-xs font-black text-slate-900">
                        {a.marca || 'Armazón'} {a.modelo ? `- Mod. ${a.modelo}` : ''}
                      </p>
                      <p className="text-[11px] text-slate-500">
                        Color / Detalles: {a.color || 'Estándar'}
                      </p>
                    </div>
                    <p className="text-sm font-black text-slate-900 shrink-0">
                      {formatearPrecio(a.precio_publico)}
                    </p>
                  </div>

                  {descObj && (() => {
                    const itemsDesc = parsearItemsDescripcion(descObj.descripcion);
                    const tituloLimpio = (descObj.titulo || '').replace(/^[-•*]\s*/, '').trim();

                    return (
                      <div className="bg-amber-50/70 border border-amber-100 rounded-lg p-2.5 text-[11px] text-amber-950">
                        {tituloLimpio && (
                          <p className="font-bold text-amber-900 flex items-center gap-1.5">
                            <SparklesIcon className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                            <span>{tituloLimpio}</span>
                          </p>
                        )}
                        {itemsDesc.length > 1 ? (
                          <ul className="mt-1.5 space-y-1 text-slate-700 pl-0.5">
                            {itemsDesc.map((it, i) => (
                              <li key={i} className="flex items-start gap-1.5 leading-snug">
                                <span className="text-amber-500 font-bold shrink-0 leading-none mt-0.5">•</span>
                                <span className="flex-1">{it}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-slate-600 mt-0.5 leading-relaxed">
                            {itemsDesc[0] || descObj.descripcion}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 5. RESUMEN DE COTIZACIÓN Y TOTALES / CONDICIONES COMERCIALES */}
      {mostrarResumenCotizacion ? (
        <div className="mt-6 bg-slate-900 text-white p-5 rounded-2xl shadow-lg border border-slate-800 space-y-3.5">
          <div className="flex items-center justify-between text-xs text-slate-300 pb-2 border-b border-slate-800">
            <span>Precio de Lista / Tarjeta:</span>
            <span className="text-sm font-bold text-slate-200">{formatearPrecio(totalPublico)}</span>
          </div>

          {tieneArmazonesCacic && cristalesSeleccionados.length > 0 ? (
            <div className="flex items-center justify-between text-xs text-emerald-400 font-semibold">
              <div className="flex items-center gap-2">
                <GiftIcon className="h-4 w-4 text-emerald-400 shrink-0" />
                <span>Descuento contado / efectivo:</span>
              </div>
              <div className="flex items-center gap-1.5">
                {descuentoOptica > 0 && (
                  <span className="bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-md font-bold">
                    {descuentoOptica}% cristales
                  </span>
                )}
                {descuentoArmazon > 0 && (
                  <span className="bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-md font-bold">
                    {descuentoArmazon}% armazón
                  </span>
                )}
              </div>
            </div>
          ) : tieneArmazonesCacic ? (
            descuentoArmazon > 0 && (
              <div className="flex items-center justify-between text-xs text-emerald-400 font-semibold">
                <div className="flex items-center gap-2">
                  <GiftIcon className="h-4 w-4 text-emerald-400 shrink-0" />
                  <span>Descuento en armazón contado / efectivo:</span>
                </div>
                <span className="bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-md font-bold">
                  {descuentoArmazon}% OFF
                </span>
              </div>
            )
          ) : (
            descuentoOptica > 0 && (
              <div className="flex items-center justify-between text-xs text-emerald-400 font-semibold">
                <div className="flex items-center gap-2">
                  <GiftIcon className="h-4 w-4 text-emerald-400 shrink-0" />
                  <span>Descuento especial por pago contado / efectivo:</span>
                </div>
                <span className="bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-md font-bold">
                  {descuentoOptica}% OFF
                </span>
              </div>
            )
          )}

          {textoCuotas && (
            <div className="flex items-center justify-between text-xs text-sky-300 font-semibold pt-1 border-t border-slate-800/60">
              <div className="flex items-center gap-2">
                <CreditCardIcon className="h-4 w-4 text-sky-400 shrink-0" />
                <span>Financiación con tarjeta:</span>
              </div>
              <span className="bg-sky-500/20 text-sky-300 px-2 py-0.5 rounded-md font-bold">
                {textoCuotas}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-slate-800/80">
            <div>
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                Total Contado / Transferencia / Efectivo
              </p>
              <p className="text-[10px] text-slate-400">
                {textoCuotas ? `Tarjeta: ${textoCuotas}` : 'Consulte por opciones de financiación y cuotas'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-black text-emerald-400 tracking-tight">
                {formatearPrecio(totalEfectivo)}
              </p>
            </div>
          </div>
        </div>
      ) : (
        /* Condiciones comerciales visibles aunque no se muestren los totales */
        (descuentoOptica > 0 || descuentoArmazon > 0 || textoCuotas) && (
          <div className="mt-6 bg-slate-900 text-white p-4.5 rounded-2xl shadow-lg border border-slate-800 space-y-2.5">
            <div className="flex items-center justify-between pb-1.5 border-b border-slate-800">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                Condiciones comerciales y formas de pago
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2.5 pt-1">
              {tieneArmazonesCacic && cristalesSeleccionados.length > 0 ? (
                <>
                  {descuentoOptica > 0 && (
                    <div className="flex items-center gap-2 bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 px-3 py-1.5 rounded-xl text-xs font-semibold">
                      <BanknotesIcon className="h-4 w-4 text-emerald-400 shrink-0" />
                      <span>Hasta {descuentoOptica}% en cristales</span>
                    </div>
                  )}
                  {descuentoArmazon > 0 && (
                    <div className="flex items-center gap-2 bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 px-3 py-1.5 rounded-xl text-xs font-semibold">
                      <BanknotesIcon className="h-4 w-4 text-emerald-400 shrink-0" />
                      <span>Hasta {descuentoArmazon}% en armazón</span>
                    </div>
                  )}
                </>
              ) : tieneArmazonesCacic ? (
                descuentoArmazon > 0 && (
                  <div className="flex items-center gap-2 bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 px-3 py-1.5 rounded-xl text-xs font-semibold">
                    <BanknotesIcon className="h-4 w-4 text-emerald-400 shrink-0" />
                    <span>Hasta {descuentoArmazon}% en armazón</span>
                  </div>
                )
              ) : (
                descuentoOptica > 0 && (
                  <div className="flex items-center gap-2 bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 px-3 py-1.5 rounded-xl text-xs font-semibold">
                    <BanknotesIcon className="h-4 w-4 text-emerald-400 shrink-0" />
                    <span>Hasta {descuentoOptica}% en efectivo</span>
                  </div>
                )
              )}
              {textoCuotas && (
                <div className="flex items-center gap-2 bg-sky-500/15 border border-sky-500/30 text-sky-300 px-3 py-1.5 rounded-xl text-xs font-semibold">
                  <CreditCardIcon className="h-4 w-4 text-sky-400 shrink-0" />
                  <span>{textoCuotas}</span>
                </div>
              )}
            </div>
          </div>
        )
      )}

      {/* 6. OBSERVACIONES */}
      {notasPresupuesto?.trim() && (
        <div className="mt-4 bg-amber-50/70 border border-amber-200/80 rounded-xl p-3 text-xs text-amber-900">
          <div className="font-bold flex items-center gap-2">
            <ChatBubbleBottomCenterTextIcon className="h-4 w-4 text-amber-700 shrink-0" />
            <span>Observaciones:</span>
          </div>
          <p className="text-slate-700 mt-1 leading-relaxed pl-6">{notasPresupuesto.trim()}</p>
        </div>
      )}

      {/* 7. FOOTER INSTITUCIONAL */}
      <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400">
        <div className="flex items-center gap-1.5">
          <CalendarDaysIcon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          <span>Presupuesto válido por {validezDias} días desde su emisión.</span>
        </div>
        <p className="font-semibold text-slate-500">
          ¡Gracias por confiar en {nombreOptica}!
        </p>
      </div>
    </div>
  );
});

PresupuestoCardImagen.propTypes = {
  optica: PropTypes.object,
  printConfig: PropTypes.object,
  cliente: PropTypes.object,
  nombreManual: PropTypes.string,
  telefonoManual: PropTypes.string,
  cristalesSeleccionados: PropTypes.array,
  lentesContactoSeleccionadas: PropTypes.array,
  armazonesSeleccionados: PropTypes.array,
  descripciones: PropTypes.array,
  totalPublico: PropTypes.number,
  totalEfectivo: PropTypes.number,
  descuentoOptica: PropTypes.number,
  descuentoArmazon: PropTypes.number,
  tieneArmazonesCacic: PropTypes.bool,
  validezDias: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  notasPresupuesto: PropTypes.string,
  mostrarResumenCotizacion: PropTypes.bool,
  cuotasSeleccionadas: PropTypes.array,
  textoCuotas: PropTypes.string,
};

export default PresupuestoCardImagen;
